// Shared profile completion utilities for Job Seeker and Employer
// Computes percentage and a checklist of missing items based on SmartWizard + dashboard expectations

const pickFirst = (...vals) => vals.find((v) => {
  if (Array.isArray(v)) return v.length > 0;
  return v !== undefined && v !== null && v !== '';
});

export function normalizeJobSeekerProfile(input) {
  const p = input?.profileData ? input.profileData : input || {};

  const personalInfo = p.personalInfo || p || {};
  const js = p.jobSeekerProfile || p || {};

  const firstName = pickFirst(personalInfo.firstName, p.firstName);
  const lastName = pickFirst(personalInfo.lastName, p.lastName);
  const phone = pickFirst(personalInfo.phone, p.phone);
  const city = pickFirst(personalInfo.address?.city, p.location?.city, p.location);

  const education = Array.isArray(p.education) ? p.education : js.education || [];
  const workExperience = Array.isArray(p.workExperience) ? p.workExperience : js.experience || [];
  const skills = Array.isArray(p.skills) ? p.skills : js.skills || [];

  const resume = pickFirst(p.cv?.fileUrl, p.cv?.url, p.cv?.file, js.resume, p.resume);

  const desiredRole = pickFirst(p.preferences?.desiredJobRole, p.preferences?.jobRole, js.jobPreferences?.desiredRole, p.desiredJobRole);
  const employmentType = pickFirst(p.preferences?.employmentType, js.jobPreferences?.jobTypes, p.employmentType) || [];
  const preferredLocations = pickFirst(p.preferences?.preferredLocations, js.jobPreferences?.workLocation, p.preferredLocations) || [];
  const expectedSalary = pickFirst(p.preferences?.expectedSalary, js.expectedSalary, p.expectedSalary) || {};

  return {
    firstName,
    lastName,
    phone,
    city,
    education: Array.isArray(education) ? education : [],
    workExperience: Array.isArray(workExperience) ? workExperience : [],
    skills: Array.isArray(skills) ? skills : [],
    resume,
    desiredRole,
    employmentType: Array.isArray(employmentType) ? employmentType : [],
    preferredLocations: Array.isArray(preferredLocations) ? preferredLocations : [],
    expectedSalary,
  };
}

export function calculateJobSeekerCompletion(profileLike) {
  const p = normalizeJobSeekerProfile(profileLike);

  const requirements = [
    { key: 'firstName', label: 'First name', ok: !!p.firstName },
    { key: 'lastName', label: 'Last name', ok: !!p.lastName },
    { key: 'phone', label: 'Phone number', ok: !!p.phone },
    { key: 'city', label: 'Location (City)', ok: !!p.city },
    { key: 'education', label: 'Education', ok: p.education.length > 0 },
    { key: 'workExperience', label: 'Work Experience', ok: p.workExperience.length > 0 },
    { key: 'skills', label: 'Skills', ok: p.skills.length > 0 },
    { key: 'resume', label: 'Resume/CV', ok: !!p.resume },
    { key: 'desiredRole', label: 'Desired Job Role', ok: !!p.desiredRole },
    { key: 'employmentType', label: 'Employment Types', ok: p.employmentType.length > 0 },
    { key: 'preferredLocations', label: 'Preferred Work Locations', ok: p.preferredLocations.length > 0 },
    { key: 'expectedSalary', label: 'Expected Salary', ok: p.expectedSalary && (p.expectedSalary.min != null || p.expectedSalary.max != null) },
  ];

  const completed = requirements.filter(r => r.ok).length;
  const percent = Math.round((completed / requirements.length) * 100);
  const missingItems = requirements.filter(r => !r.ok).map(r => ({ key: r.key, label: r.label }));

  return { percent, total: requirements.length, completed, missingItems };
}

// Normalize employer profile across various shapes
export function normalizeEmployerProfile(input) {
  const p = input?.profileData ? input.profileData : input || {};

  const companyInfo = p.companyInfo || p.employerProfile || p || {};
  const rawAddress = companyInfo.address || companyInfo.companyAddress || p.companyAddress || p.officeAddress || p.address || {};
  const address = (() => {
    if (!rawAddress) return {};
    if (typeof rawAddress === 'string') return { street: rawAddress };
    const street = rawAddress.street || rawAddress.address || p.street;
    return {
      street,
      city: rawAddress.city || p.city,
      province: rawAddress.province || rawAddress.state || p.state,
      country: rawAddress.country || p.country,
      postalCode: rawAddress.postalCode || rawAddress.zipCode || p.zipCode,
    };
  })();
  const contact = companyInfo.contactInfo || p.contactInfo || {};
  const representative = p.representative || {
    name: p.representativeName,
    position: p.representativePosition,
  };

  return {
    companyName: companyInfo.companyName || p.companyName,
    industry: companyInfo.industry || companyInfo.industryType || p.industry || p.industryType,
    companySize: companyInfo.companySize || p.companySize,
    website: companyInfo.website || p.website,
    description: companyInfo.companyDescription || companyInfo.description || p.companyDescription || p.description,
    logo: companyInfo.companyLogo || companyInfo.logo || p.companyLogo || p.logo,
    email: contact.email || p.email,
    phone: contact.phone || companyInfo.companyPhone || p.companyPhone || p.phone,
    address: {
      street: address.street,
      city: address.city,
      province: address.province,
      country: address.country,
      postalCode: address.postalCode,
    },
    representativeName: representative?.name,
    representativePosition: representative?.position,
    jobCategories: Array.isArray(companyInfo.jobCategories) ? companyInfo.jobCategories : (Array.isArray(p.jobCategories) ? p.jobCategories : []),
    foundedYear: companyInfo.foundedYear || p.foundedYear,
    specialties: Array.isArray(companyInfo.specialties) ? companyInfo.specialties : (Array.isArray(p.specialties) ? p.specialties : []),
    isVerified: !!(companyInfo.isVerified || p.isVerified || ((companyInfo.verificationStatus || p.verificationStatus) === 'approved')),
  };
}

// Employer completion calculation based on SmartWizard requirements
export function calculateEmployerCompletion(profileLike) {
  const p = normalizeEmployerProfile(profileLike);

  const hasAddress = !!(p.address && (p.address.street || p.address.city || p.address.country));

  const requirements = [
    { key: 'companyName', label: 'Company Name', ok: !!p.companyName },
    { key: 'industry', label: 'Industry', ok: !!p.industry },
    { key: 'companySize', label: 'Company Size', ok: !!p.companySize },
    { key: 'phone', label: 'Company Phone', ok: !!p.phone },
    { key: 'address', label: 'Office Address', ok: hasAddress },
    { key: 'representativeName', label: 'Representative Name', ok: !!p.representativeName },
    { key: 'representativePosition', label: 'Representative Position', ok: !!p.representativePosition },
  ];

  const completed = requirements.filter(r => r.ok).length;
  const percent = Math.round((completed / requirements.length) * 100);
  const missingItems = requirements.filter(r => !r.ok).map(r => ({ key: r.key, label: r.label }));

  return { percent, total: requirements.length, completed, missingItems };
}